<template>
    <section>
        <VCard>
            <VCardText class="d-flex flex-wrap gap-4">
                <VSpacer />

                <div class="d-flex align-center flex-wrap gap-4">
                    <!-- 👉 Search  -->
                    <AppTextField v-model="searchQuery" :placeholder="t('admin_notifications.search_by_name')"
                        density="compact" prepend-inner-icon="tabler-search" style="inline-size: 20rem;" clearable />

                    <!-- 👉 Add status button -->
                    <VBtn v-if="$can('create', 'status')" color="success" prepend-icon="tabler-plus"
                        @click="openStatusModal()">
                        {{ $t('admin_notifications.add_new') }}
                    </VBtn>
                </div>
            </VCardText>

            <VDivider />

            <!-- SECTION datatable -->
            <VDataTableServer v-model:items-per-page="itemsPerPage" v-model:page="page" :items-per-page-options="[
                { value: 10, title: '10' },
                { value: 20, title: '20' },
                { value: 50, title: '50' },
                { value: -1, title: '$vuetify.dataFooter.itemsPerPageAll' },
            ]" :items="statuses" :items-length="totalStatuses" :headers="headers" class="text-no-wrap styled-table"
                @update:options="updateOptions">
                <!-- Status Name -->
                <template #item.name="{ item }">
                    <VChip :color="item.color || 'primary'" rounded="sm" size="small" variant="tonal">
                        {{ item.title }}
                    </VChip>
                </template>

                <!-- Status Type -->
                <template #item.type="{ item }">
                    <div v-if="item.type && Array.isArray(item.type) && item.type.length > 0"
                        class="d-flex flex-wrap gap-1">
                        <VChip v-for="type in item.type" rounded="sm" :key="type" :color="getTypeColor(type)"
                            size="small" variant="tonal">
                            {{ getTypeLabel(type) }}
                        </VChip>
                    </div>

                </template>



                <!-- Actions -->
                <template #item.actions="{ item }">
                    <div class="d-flex gap-2">
                        <IconBtn v-if="$can('edit', 'status')" @click="openStatusModal(item)">
                            <VIcon icon="tabler-edit" />
                        </IconBtn>
                        <!-- <IconBtn v-if="$can('delete', 'status')" color="error" @click="deleteStatus(item)">
                            <VIcon icon="tabler-trash" />
                        </IconBtn> -->
                    </div>
                </template>

                <template #bottom>
                    <VDivider />

                    <div class="d-flex align-center justify-sm-space-between justify-center flex-wrap gap-3 pa-5 pt-3">
                        <p class="text-sm text-disabled mb-0">
                            {{ paginationMeta({ page, itemsPerPage }, totalStatuses) }}
                        </p>

                        <VPagination v-model="page" :length="Math.ceil(totalStatuses / itemsPerPage)"
                            :total-visible="$vuetify.display.xs ? 1 : Math.ceil(totalStatuses / itemsPerPage)">
                            <template #prev="slotProps">
                                <VBtn variant="tonal" color="default" v-bind="slotProps" :icon="false">
                                    {{ $t('admin_notifications.previous') }}
                                </VBtn>
                            </template>

                            <template #next="slotProps">
                                <VBtn variant="tonal" color="default" v-bind="slotProps" :icon="false">
                                    {{ $t('admin_notifications.next') }}
                                </VBtn>
                            </template>
                        </VPagination>
                    </div>
                </template>
            </VDataTableServer>
        </VCard>

        <!-- Status Modal -->
        <VDialog v-model="isStatusModalVisible" max-width="600px">
            <VCard>
                <VCardTitle>
                    {{ selectedStatus ? t('admin_notifications.edit_status') : t('admin_notifications.add_new_status')
                    }}
                </VCardTitle>

                <VCardText>
                    <!-- Error Alert -->
                    <VAlert v-if="Object.keys(errors).length > 0" icon="tabler-alert-circle" type="error"
                        variant="tonal" class="mb-4" closable @click:close="errors = {}">
                        <template #title>
                            {{ errors.general ? t('admin_notifications.error') :
                                t('admin_notifications.validation_errors') }}
                        </template>

                        <ul class="mb-0">
                            <li v-for="(error, field) in errors" :key="field">
                                <span v-if="field === 'general'">{{ error }}</span>
                                <span v-else><strong>{{ field }}:</strong> {{ error }}</span>
                            </li>
                        </ul>
                    </VAlert>

                    <VForm ref="formRef" @submit.prevent="onSubmit">
                        <VRow>
                            <VCol cols="12">
                                <VTextField v-model="form.title" :label="t('admin_notifications.status_name')"
                                    :placeholder="t('admin_notifications.enter_status_name')"
                                    :rules="[v => !!v || t('admin_notifications.name_required')]"
                                    :error-messages="errors.title" required />
                            </VCol>

                            <VCol cols="12">
                                <VSelect v-model="form.type" multiple :label="t('admin_notifications.status_type')"
                                    :placeholder="t('admin_notifications.select_type')" :items="typeOptions"
                                    item-title="label" item-value="value" :error-messages="errors.type" />
                            </VCol>

                            <VCol cols="12">
                                <VTextField v-model="form.sort_order" :label="t('admin_notifications.sort_order')"
                                    placeholder="0" type="number" min="0" :error-messages="errors.sort_order" />
                            </VCol>

                            <VCol cols="12">
                                <div class="d-flex flex-column gap-2">
                                    <label class="text-body-2">{{ $t('admin_notifications.status_color') }}</label>
                                    <div class="d-flex align-center gap-3">
                                        <input v-model="form.color" type="color" class="color-picker"
                                            :style="{ backgroundColor: form.color || '#1976d2' }" />
                                        <VTextField v-model="form.color" placeholder="#1976d2" density="compact"
                                            :error-messages="errors.color" hide-details="auto" />
                                    </div>
                                </div>
                            </VCol>


                        </VRow>
                    </VForm>
                </VCardText>


                <VCardActions class="pa-4 d-flex gap-4 justify-end">
                    <VBtn style="width: 150px;" variant="flat" @click="onSubmit" :loading="isSubmitting">
                        {{ $t('admin_notifications.submit') }}
                    </VBtn>
                    <VBtn color="secondary" variant="tonal" style="width: 150px;" @click="closeStatusModal">
                        {{ $t('admin_notifications.cancel') }}
                    </VBtn>

                </VCardActions>

            </VCard>
        </VDialog>
    </section>
</template>

<script setup>
definePage({
    meta: {
        action: 'view',
        subject: 'status',
    },
})

import { VDataTableServer } from 'vuetify/labs/VDataTable'
import { paginationMeta } from '@api-utils/paginationMeta'
import $api from "~/utils/api";
import { onMounted, ref, watch, computed } from "vue";
import { useI18n } from 'vue-i18n';
import { useHead } from '@unhead/vue'

const { t } = useI18n()

// Set page title
useHead({
    title: t('menu.statuses'),
    meta: [
        { name: 'description', content: t('admin_notifications.statuses_description') }
    ]
})

// Data table options
const itemsPerPage = ref(100000)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()

// Search
const searchQuery = ref('')
const debouncedSearchQuery = ref('')
let searchTimeout = null

const debounceSearch = (value) => {
    clearTimeout(searchTimeout)
    searchTimeout = setTimeout(() => {
        debouncedSearchQuery.value = value
    }, 500)
}

const updateOptions = options => {
    page.value = options.page
    sortBy.value = options.sortBy[0]?.key
    orderBy.value = options.sortBy[0]?.order
}

// Headers
const headers = computed(() => [
    {
        title: '#',
        key: 'sort_order',
        sortable: false,
        width: '80px',
    },
    {
        title: t('admin_notifications.name'),
        key: 'name',
        sortable: false,
    },
    {
        title: t('admin_notifications.type'),
        key: 'type',
        sortable: false,
    },
    {
        title: t('admin_notifications.actions'),
        key: 'actions',
        sortable: false,
        width: '120px',
    },
])

const {
    data: statusesData,
    execute: fetchStatuses,
} = await useApi(createUrl('/api/statuses', {
    query: {
        q: debouncedSearchQuery,
        per_page: itemsPerPage,
        page,
        sortBy,
        orderBy,
    },
}))

const statuses = computed(() => statusesData.value?.data || [])
const totalStatuses = computed(() => statusesData.value?.meta?.total || 0)



// Modal state
const isStatusModalVisible = ref(false)
const selectedStatus = ref(null)
const isSubmitting = ref(false)
const formRef = ref()
const errors = ref({})

// Form data
const form = ref({
    title: '',
    type: [],
    color: '',
    sort_order: 0,
})

// Type options
const typeOptions = computed(() => [
    { value: 'prepress', label: t('admin_notifications.prepress') },
    { value: 'production', label: t('admin_notifications.production') },
    { value: 'invoicing', label: t('admin_notifications.invoicing') },
])

// Helper functions for type display
const getTypeLabel = (type) => {
    if (Array.isArray(type)) {
        return type.map(t => {
            const option = typeOptions.value.find(opt => opt.value === t)
            return option ? option.label : t
        }).join(', ')
    }
    const option = typeOptions.value.find(opt => opt.value === type)
    return option ? option.label : type || ''
}

const getTypeColor = (type) => {
    switch (type) {
        case 'prepress':
            return 'info'
        case 'production':
            return 'warning'
        case 'invoicing':
            return 'success'
        default:
            return 'secondary'
    }
}


// Watch for search query changes to apply debounce
watch(searchQuery, (newValue) => {
    debounceSearch(newValue)
})



// Modal methods
const openStatusModal = (status = null) => {
    selectedStatus.value = status
    if (status) {
        form.value = { ...status }
    } else {
        form.value = {
            title: '',
            type: [],
            color: '',
            sort_order: 0,
        }
    }
    isStatusModalVisible.value = true
}

const closeStatusModal = () => {
    isStatusModalVisible.value = false
    selectedStatus.value = null
    form.value = {
        title: '',
        type: [],
        color: '',
        sort_order: 0,
    }
    errors.value = {} // Очищаем ошибки
}

const onSubmit = async () => {
    const { valid } = await formRef.value.validate()
    if (!valid) return

    isSubmitting.value = true
    errors.value = {} // Очищаем предыдущие ошибки

    try {
        if (selectedStatus.value) {
            // Update
            await $api.post(`/statuses/${selectedStatus.value.id}`, form.value)
        } else {
            // Create
            await $api.post('/statuses', form.value)
        }

        closeStatusModal()
        await fetchStatuses()
    } catch (error) {
        console.error('Ошибка при сохранении статуса:', error)

        // Обрабатываем ошибки валидации с сервера
        if (error.response?.data?.errors) {
            errors.value = error.response.data.errors
        } else if (error.response?.data?.message) {
            // Общая ошибка
            errors.value = { general: error.response.data.message }
        } else {
            // Неизвестная ошибка
            errors.value = { general: 'Произошла неизвестная ошибка' }
        }
    } finally {
        isSubmitting.value = false
    }
}

// Delete status
const deleteStatus = async (status) => {
    if (!confirm(t('admin_notifications.confirm_delete_status', { name: status.title }))) return

    try {
        await $api.delete(`/statuses/${status.id}`)
        await fetchStatuses()
    } catch (error) {
        console.error('Ошибка при удалении статуса:', error)
    }
}

// Load data on mount
onMounted(() => {
    fetchStatuses()
})
</script>

<style scoped>
.color-picker {
    width: 48px;
    height: 48px;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    cursor: pointer;
    outline: none;
}

.color-picker::-webkit-color-swatch-wrapper {
    padding: 0;
}

.color-picker::-webkit-color-swatch {
    border: none;
    border-radius: 6px;
}

.styled-table :deep(.v-data-table__th) {
    font-weight: bold !important;
    position: relative;
}

.styled-table :deep(.v-data-table__th:not(:last-child))::after {
    content: '';
    position: absolute;
    right: 0;
    top: 30%;
    bottom: 30%;
    width: 2px;
    background-color: rgba(var(--v-theme-on-surface), 0.12);
}

.styled-table :deep(.v-data-table-header__content) {
    font-weight: bold !important;
}
</style>
